import { useEffect } from 'react'

import useCurrentUser from './useCurrentUser'

type IntercomType = {
  hideWidget?: boolean,
  horizontalPadding?: number,
}

export type TrackEventParams = {
  eventName: string,
  eventPayload?: Record<string, string | boolean>,
}

const useIntercom = (options: IntercomType = {}) => {
  // TODO: When the intercom flipper key is removed, default the hideWidget option to false
  const { hideWidget, horizontalPadding = 20 } = options

  function loadIntercom(appId: string, settings: Record<string, unknown> = {}) {
    if (typeof window === 'undefined') return

    if (window.Intercom && typeof window.Intercom === 'function'){
      window.Intercom('update', settings)
      return
    }

    window.intercomSettings = { app_id: appId, ...settings }

    const scriptId = 'intercom-script'
    if (document.getElementById(scriptId)) return

    const script = document.createElement('script')
    script.id = scriptId
    script.async = true
    script.src = `https://widget.intercom.io/widget/${appId}`
    document.head.appendChild(script)
  }

  const {
    currentUser: {
      email, full_name, id, mobile_number, cache_key,
    },
    loaded, brand, roleTitle
  } = useCurrentUser()

  useEffect(() => {
    const settings = {
      app_id: import.meta.env.REACT_APP_INTERCOM_APP_ID,
      brand: brand?.title || "No brand",
      custom_attributes: {
        business_unit: 'Realhub',
        onboarding: false,
      },
      email,
      horizontal_padding: horizontalPadding,
      name: full_name,
      phone: mobile_number,
      role: roleTitle,
      user_id: id,
      z_index: 997,
    }

    if (!hideWidget){
      loadIntercom(import.meta.env.REACT_APP_INTERCOM_APP_ID!, settings)
    }
  }, [cache_key, hideWidget, loaded])

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (!window.Intercom || hideWidget) return

      const target = event.target as HTMLElement | null
      if (!target) return

      const clickedInsideIntercom = !!target.closest('[id^="intercom"], [class*="intercom"]')

      if (!clickedInsideIntercom){
        window.Intercom?.('hide')
      }
    }

    document.addEventListener('click', onDocClick, { capture: true, passive: true })
    return () => document.removeEventListener('click', onDocClick, { capture: true })
  }, [hideWidget])

  const trackEvent = (params: TrackEventParams) => {
    const { eventName, eventPayload = {} } = params
    if (window.Intercom){
      const extendedPayload = {
        ...eventPayload,
        business_unit: 'Realhub',
        role: roleTitle,
        user_id: id || 'Unknown User',
      }
      window.Intercom('trackEvent', eventName, extendedPayload)
    }
  }

  return {
    callbacks: {
      trackEvent: (params: TrackEventParams) => {
        trackEvent(params)
      },
    },
  }
}

export default useIntercom
